import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import { Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";

import { RowAction } from "@/components/row-actions-menu";
import { notifyError } from "@/lib/notify-error";
import { cn } from "@/lib/utils";

type RetryKind = FunctionReturnType<typeof api.projectDocumentProcessing.retryRevisionProcessing>["kind"];

/** Success toast per `retryRevisionProcessing` result kind; "noop" restarts nothing. */
const RETRY_TOASTS: Record<Exclude<RetryKind, "noop">, string> = {
  classification: "Classification restarted",
  document_intelligence: "Plan indexing restarted",
  schedule_extraction: "Schedule extraction restarted",
  packet_build: "Rebuilding the selected pages",
};

/** Retries a revision's failed processing, tracking which revisions are in flight. */
export function useRevisionRetry() {
  const retryRevisionProcessing = useMutation(api.projectDocumentProcessing.retryRevisionProcessing);
  const [retryingIds, setRetryingIds] = useState<ReadonlySet<string>>(new Set());

  const setRetrying = (revisionId: string, retrying: boolean) =>
    setRetryingIds((current) => {
      const next = new Set(current);
      if (retrying) next.add(revisionId);
      else next.delete(revisionId);
      return next;
    });

  const retryRevision = async (revisionId: Id<"projectDocumentRevisions">) => {
    if (retryingIds.has(revisionId)) return;
    setRetrying(revisionId, true);
    try {
      const result = await retryRevisionProcessing({ revisionId });
      if (result.kind === "noop") {
        toast.info("This document type has no processing to retry");
      } else {
        toast.success(RETRY_TOASTS[result.kind]);
      }
    } catch (error) {
      notifyError(error, "Failed to retry processing");
    } finally {
      setRetrying(revisionId, false);
    }
  };

  return {
    retryRevision,
    isRetrying: (revisionId: Id<"projectDocumentRevisions">) => retryingIds.has(revisionId),
  };
}

export interface RevisionRetry {
  isRetrying: boolean;
  onRetry: () => void;
}

/** "Retry processing" row-menu item; it stays open and disabled while the retry is in flight. */
export function RetryProcessingAction({ isRetrying, onRetry }: RevisionRetry) {
  return (
    <RowAction
      icon={isRetrying ? RetrySpinner : RotateCw}
      disabled={isRetrying}
      onSelect={(event) => {
        event.preventDefault();
        onRetry();
      }}
    >
      Retry processing
    </RowAction>
  );
}

function RetrySpinner({ className }: { className?: string }) {
  return <Loader2 className={cn(className, "animate-spin")} />;
}
